import React from 'react';
import { Deployment } from '../../types/api';
import { CheckCircle, XCircle, Clock, Loader2, RotateCcw, AlertCircle } from 'lucide-react';
import clsx from 'clsx';

interface DeploymentStatusProps {
  deployment: Deployment;
  onCancel?: (deploymentId: string) => void;
  onRollback?: (deploymentId: string) => void;
}

export function DeploymentStatus({ deployment, onCancel, onRollback }: DeploymentStatusProps) {
  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'text-yellow-600',
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
    },
    in_progress: {
      icon: Loader2,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      animate: 'animate-spin',
    },
    completed: {
      icon: CheckCircle,
      color: 'text-green-600',
      bg: 'bg-green-50',
      border: 'border-green-200',
    },
    failed: {
      icon: XCircle,
      color: 'text-red-600',
      bg: 'bg-red-50',
      border: 'border-red-200',
    },
    rolled_back: {
      icon: RotateCcw,
      color: 'text-gray-600',
      bg: 'bg-gray-50',
      border: 'border-gray-200',
    },
  };

  const config = statusConfig[deployment.status];
  const Icon = config.icon;

  const createdDate = new Date(deployment.createdAt).toLocaleString();
  const completedDate = deployment.completedAt
    ? new Date(deployment.completedAt).toLocaleString()
    : null;

  const canCancel = deployment.status === 'pending' || deployment.status === 'in_progress';
  const canRollback = deployment.status === 'completed';

  const totalRepos = deployment.targetRepos?.length || 0;
  const successCount = deployment.stats?.success || 0;
  const failedCount = deployment.stats?.failed || 0;
  const pendingCount = deployment.stats?.pending || 0;

  const progressPercentage = totalRepos > 0
    ? ((successCount + failedCount) / totalRepos) * 100
    : 0;

  return (
    <div className={clsx('border rounded-lg p-6', config.bg, config.border)}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <Icon className={clsx('h-6 w-6 mt-1', config.color, config.animate)} />
          <div>
            <h3 className="font-semibold text-gray-900">
              {deployment.featureName}
              {deployment.version && <span className="text-gray-500 ml-2">v{deployment.version}</span>}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              ID: {deployment.id}
            </p>
          </div>
        </div>

        <span className={clsx('px-3 py-1 rounded-full text-sm font-medium capitalize', config.color)}>
          {deployment.status.replace('_', ' ')}
        </span>
      </div>

      {/* Progress Bar */}
      {deployment.status === 'in_progress' && (
        <div className="mb-4">
          <div className="flex justify-between text-sm text-gray-600 mb-2">
            <span>Progress</span>
            <span>{progressPercentage.toFixed(0)}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-blue-600 transition-all duration-300"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats */}
      {deployment.stats && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-green-600">{successCount}</div>
            <div className="text-xs text-gray-500">Success</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-red-600">{failedCount}</div>
            <div className="text-xs text-gray-500">Failed</div>
          </div>
          <div className="text-center p-3 bg-white rounded-lg">
            <div className="text-2xl font-bold text-yellow-600">{pendingCount}</div>
            <div className="text-xs text-gray-500">Pending</div>
          </div>
        </div>
      )}

      {/* Target Repos */}
      {deployment.targetRepos && deployment.targetRepos.length > 0 && (
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">
            Target Repositories ({deployment.targetRepos.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {deployment.targetRepos.slice(0, 5).map((repo) => (
              <span key={repo} className="px-2 py-1 bg-white text-gray-700 rounded text-xs">
                {repo}
              </span>
            ))}
            {deployment.targetRepos.length > 5 && (
              <span className="px-2 py-1 bg-white text-gray-500 rounded text-xs">
                +{deployment.targetRepos.length - 5} more
              </span>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {deployment.error && (
        <div className="mb-4 p-3 bg-white border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-900">Error</p>
            <p className="text-sm text-red-700 mt-1">{deployment.error}</p>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="flex items-center justify-between text-sm text-gray-600 pt-4 border-t border-gray-200">
        <div>
          <span className="font-medium">Created:</span> {createdDate}
        </div>
        {completedDate && (
          <div>
            <span className="font-medium">Completed:</span> {completedDate}
          </div>
        )}
      </div>

      {/* Actions */}
      {(canCancel || canRollback) && (
        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
          {canCancel && onCancel && (
            <button
              onClick={() => onCancel(deployment.id)}
              className="px-4 py-2 bg-white border border-red-300 text-red-700 rounded-lg hover:bg-red-50 transition-colors"
            >
              Cancel Deployment
            </button>
          )}
          {canRollback && onRollback && (
            <button
              onClick={() => onRollback(deployment.id)}
              className="px-4 py-2 bg-white border border-yellow-300 text-yellow-700 rounded-lg hover:bg-yellow-50 transition-colors"
            >
              <RotateCcw className="h-4 w-4 inline mr-2" />
              Rollback
            </button>
          )}
        </div>
      )}
    </div>
  );
}
